import React, { useState, useEffect } from 'react';
import Modal from 'global/components/modal';
import {
  TmContainer,
  RealmResetDiv,
  Header,
  Grouping,
  NoneYet,
  FlexRow,
  TemplateButtonStyled,
  TemplateButton2,
} from './styles';
import CloseIcon from '@mui/icons-material/Close';
import { Button, Icon, IconButton } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { get, size, set } from 'global/lib/lodash';
import setUserRealm from 'redux/actions/userRealm';
import { userHasPermissions, getAccountSetting } from 'redux/actions/userLogin';
import { SETTINGS } from 'feature/admin/account-settings';
import Styles, { Loader, useScreen } from 'global/styles';
import Alert from '@mui/material/Alert';
import { Access } from 'global/components/droprealms';
import { withRouter } from 'react-router-dom';
import { useUser } from 'global/hooks/useUser';
import { getOnlyTiersFromScorecards } from '../index';
import { Text } from '../../../../global/components/text';
import Form from '../../../../global/components/form';
import useRetrieveData from 'global/hooks/useRetrieveData';

const TemplateButton = (props) => {
  const { title, secondButton, onClick, subtitle } = props;

  return (
    <FlexRow>
      <TemplateButtonStyled startIcon={<Icon>add</Icon>} onClick={onClick}>
        <div style={{ textAlign: 'left' }}>
          <div>{title}</div>
          {!!subtitle && (
            <div>
              <Text type="label_small" italic>{`for ${subtitle}`}</Text>
            </div>
          )}
        </div>
      </TemplateButtonStyled>
      {secondButton && (
        <TemplateButton2 as={TemplateButtonStyled} onClick={secondButton.onClick}>
          <Icon>{secondButton.icon}</Icon>
        </TemplateButton2>
      )}
    </FlexRow>
  );
};

const firstBracketMatch = (...strings) => {
  for (const str of strings) {
    const match = str?.match(/\[(.*?)\]/);
    if (match) return match[1];
  }
  return undefined;
};

const exceptBracketMatch = (string) => {
  return string?.replace(/\[.*?\]/, '');
};

const RealmReset = (props) => {
  const realm = useSelector((state) => get(state, 'auth.realm'));
  const dispatch = useDispatch();

  const onReset = () => {
    dispatch(setUserRealm(null));
  };

  const hasRealm = get(realm, 'id');

  return !hasRealm ? null : (
    <RealmResetDiv>
      Showing available templates for <Icon>room</Icon>
      {get(realm, 'name')} <Button onClick={onReset}>RESET</Button>
    </RealmResetDiv>
  );
};

export default withRouter((props) => {
  const { templates, onDefaultSelect, startTemplateCreate } = props;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();
  const [selected, setSelected] = useState();
  const { isMobileXS } = useScreen();
  const { me } = useUser();
  const realm = useSelector((state) => get(state, 'auth.realm'));
  const styles = Styles();
  let [selRealm, setSelRealm] = useState();
  const [selectLotLevelInfo, setSelectLotLevelInfo] = useState();

  const hasPerm = userHasPermissions('createReportTemplates');
  const customAllowed = getAccountSetting(SETTINGS.customAssessmentsAllowed) != '0';
  const showCustom = customAllowed || hasPerm;
  const tempCopy = firstBracketMatch(selected?.name);
  // PFM-2574: lot-level default title uses the [bracket] shorthand when present,
  // otherwise the full template name (restores pre-PFM-2299 behavior).
  const titleSuffix = tempCopy || exceptBracketMatch(selected?.name)?.trim();

  const onEdit = (temp) => {
    props.history.push(`assessment-template/${temp.id}`);
  };

  const users = useRetrieveData(
    { url: '/users' },
    {
      useEffect: true,
      waitFor: !!selRealm && !!selRealm.id,
      build: ['users'],
      params: { filter: { content_realm_id: get(selRealm, 'id') } },
      useCache: { reload: 1 },
    }
  );

  const startSubmit = (temp, _r) => {
    try {
      const r = _r[Object.keys(_r)[0]];
      selRealm = r;
      setSelRealm(r);

      if (r.tier === 'lot') {
        return setSelectLotLevelInfo({});
      }

      submitTemplate(temp);
    } catch (err) {}
  };

  const submitTemplate = async (temp = selected, values = {}) => {
    try {
      setLoading(true);
      const scorecards = {};
      Object.keys(get(temp, 'scores') || {}).forEach((k) => {
        set(scorecards, `scorecard_${k}`, k);
      });
      const err =
        props.onSelectTemplate &&
        (await props.onSelectTemplate({
          ...temp,
          ...scorecards,
          generalVisibility: get(temp, 'visibility'),
          template_id: temp.id,
          realmId: selRealm.id,
          accessRealmTier: get(selRealm, 'tier', 'division'),
          reportName: `${selRealm?.name || exceptBracketMatch(temp?.name)}${tempCopy ? ` - ${tempCopy}` : ''}`,
          ...values,
        }));

      if (err) {
        setError(err.message);
      }
    } catch (err) {
      console.error('submitTemplate failed', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const selectTemplate = (temp) => {
    setSelected(temp);
  };

  useEffect(() => {
    if (props.open) {
      setSelected();
      setError();
      setLoading(false);
      setSelectLotLevelInfo();
    }
  }, [props.open]);

  const all = get(templates, 'data', []);
  const global = all.filter((t) => get(t, 'status') === 'global_template');
  const mine = all.filter((t) => get(t, 'status') === 'template' && (get(t, 'authors', []).find((u) => u.id === me.id) || get(t, 'editors', []).find((u) => u.id === me.id)));

  const hasMine = size(mine) > 0;

  const onlyTiers = (() => {
    if (!selected) {
      return ['division', 'community', 'lot'];
    }

    return getOnlyTiersFromScorecards(get(selected, 'scorecards', []));
  })();

  return (
    <Modal title={selected ? `NEW ${(get(selected, 'name') || '').toUpperCase()}` : 'CREATE ASSESSMENT'} {...props}>
      {loading && (
        <TmContainer isMobileXS={isMobileXS} className={styles.centerColumn}>
          <Loader />
        </TmContainer>
      )}

      {!loading && selected && !selectLotLevelInfo && (
        <div style={{ width: '100%', height: '100%' }}>
          <Access
            onlyTiers={onlyTiers}
            singleSelect={true}
            canAdd
            onSelectedChange={(sel) => {
              startSubmit(selected, sel);
            }}
            openToSelected={true}
            currentRealm={realm || selected.realm}
            openToChildren
          />
        </div>
      )}

      {!loading && selectLotLevelInfo && (
        <div>
          <Form
            onSubmit={(vals) => {
              submitTemplate(selected, vals);
            }}
            showAllScreens
            canSubmit={true}
            useCheckContinue
            submitBtnBoxStyle={{ position: 'relative' }}
            screens={[
              {
                inputs: [
                  {
                    id: 'reportName',
                    type: 'TextField',
                    defaultValue: () => `${selRealm?.name || exceptBracketMatch(selected?.name)}${titleSuffix ? ` - ${titleSuffix}` : ''}`,
                    props: {
                      label: 'Title',
                      required: true,
                    },
                  },
                  {
                    id: 'assignees',
                    type: 'UserSelect',
                    defaultValue: () => null,
                    data: () => users.data,
                    props: {
                      label: 'Assignee',
                      required: true,
                      multiple: false,
                    },
                  },
                ],
              },
            ]}
          />
        </div>
      )}

      {!loading && !selected && !selectLotLevelInfo && (
        <TmContainer isMobileXS={isMobileXS}>
          {error && (
            <Alert
              severity="error"
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setError();
                  }}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
            >
              {error}
            </Alert>
          )}
          {showCustom && <TemplateButton title="Custom Assessment" onClick={onDefaultSelect} />}

          <Grouping>
            <RealmReset />
            {!!size(global) && (
              <>
                <Header>ALL TEMPLATES</Header>
                {global.map((temp, i) => (
                  <TemplateButton
                    key={i}
                    onClick={() => selectTemplate(temp)}
                    title={get(temp, 'name')}
                    subtitle={get(temp, 'realm.name')}
                    secondButton={{ icon: 'visibility', onClick: () => onEdit(temp) }}
                  />
                ))}
              </>
            )}
          </Grouping>

          {(hasPerm || hasMine) && (
            <Grouping>
              <FlexRow>
                <Header>MY TEMPLATES</Header>
                {hasPerm && (
                  <TemplateButton2 as={TemplateButtonStyled} onClick={startTemplateCreate}>
                    <Icon>add</Icon>
                  </TemplateButton2>
                )}
              </FlexRow>
              {mine.map((temp, i) => (
                <TemplateButton
                  key={i}
                  onClick={() => selectTemplate(temp)}
                  title={get(temp, 'name')}
                  subtitle={get(temp, 'realm.name')}
                  secondButton={{ icon: 'edit', onClick: () => onEdit(temp) }}
                />
              ))}
              {!hasMine && <NoneYet>{`You haven't created any assessment templates yet`}</NoneYet>}
            </Grouping>
          )}
        </TmContainer>
      )}
    </Modal>
  );
});
